import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ArticleJsonLd, BreadcrumbJsonLd } from './JsonLd';

interface BlogPost {
  id: string;
  title: string;
  content: string;
  cover_image: string;
  created_at: string;
  meta_description: string;
}

function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('slug', slug)
          .single();

        if (error) throw error;
        setPost(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-md">
        {error || 'Blog post not found'}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} - OldCoinage.com Blog</title>
        <meta name="description" content={post.meta_description} />
      </Helmet>
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://oldcoinage.com/' },
          { name: 'Blog', url: 'https://oldcoinage.com/blog' },
          { name: post.title, url: `https://oldcoinage.com/blog/${slug}` }
        ]}
      />
      <ArticleJsonLd
        headline={post.title}
        description={post.meta_description}
        image={post.cover_image}
        url={`https://oldcoinage.com/blog/${slug}`}
        datePublished={post.created_at}
      />

      <article className="max-w-4xl mx-auto">
        <div className="aspect-w-16 aspect-h-9 mb-8">
          <img
            src={post.cover_image}
            alt={post.title}
            className="w-full h-96 object-cover rounded-lg shadow-lg"
            loading="lazy"
          />
        </div>
        
        <div className="mt-8">
          <time className="text-gray-500">
            {format(new Date(post.created_at), 'MMMM d, yyyy')}
          </time>
          <h1 className="mt-2 text-4xl font-bold text-gray-900">{post.title}</h1>
          
          <div className="mt-8 prose prose-lg prose-indigo max-w-none">
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </div>
        </div>
      </article>
    </>
  );
}

export default BlogPost